/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MessagesSquare, RotateCcw, Radio } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ConfirmDialog } from '@/components/confirm-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

import {
  getTrialMessages,
  listTrialActivity,
  resetTrialActivity,
  type TrialUsage,
} from './api'

function formatTime(unixSeconds: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleString()
}

function TrialMessagesDialog({
  ip,
  open,
  onOpenChange,
}: {
  ip: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['trial-activity-messages', ip],
    queryFn: () => getTrialMessages(ip as string),
    enabled: !!ip && open,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[80vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='font-mono text-sm'>{ip}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className='space-y-3'>
            <Skeleton className='h-16 w-full' />
            <Skeleton className='h-16 w-full' />
          </div>
        ) : !data?.data?.length ? (
          <p className='text-muted-foreground py-8 text-center text-sm'>
            {t('No messages found for this IP.')}
          </p>
        ) : (
          <div className='space-y-4'>
            {data.data.map((m, i) => (
              <div key={i} className='space-y-2'>
                <div className='bg-blue-500/10 text-foreground rounded-lg rounded-tr-sm px-3 py-2 text-sm'>
                  {m.question}
                </div>
                <div className='bg-muted rounded-lg rounded-tl-sm px-3 py-2 text-sm'>
                  {m.reply}
                </div>
                <p className='text-muted-foreground text-right text-[10px]'>
                  {formatTime(m.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function TrialActivity() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [viewingIp, setViewingIp] = useState<string | null>(null)
  const [resetTarget, setResetTarget] = useState<TrialUsage | null>(null)
  const [resetting, setResetting] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['trial-activity'],
    queryFn: listTrialActivity,
  })

  const handleReset = async () => {
    if (!resetTarget) return
    setResetting(true)
    try {
      await resetTrialActivity(resetTarget.ip)
      toast.success(t('IP reset — they can use the trial chat again.'))
      queryClient.invalidateQueries({ queryKey: ['trial-activity'] })
    } catch {
      toast.error(t('Failed to reset this IP.'))
    } finally {
      setResetting(false)
      setResetTarget(null)
    }
  }

  const usages = data?.data ?? []

  return (
    <div className='mx-auto max-w-4xl p-4 md:p-6'>
      <div className='mb-6 flex items-center gap-3'>
        <div className='flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/15 to-cyan-500/15 text-blue-500'>
          <Radio className='size-5' />
        </div>
        <div>
          <h1 className='text-xl font-semibold tracking-tight md:text-2xl'>
            {t('Trial chat activity')}
          </h1>
          <p className='text-muted-foreground text-sm'>
            {t('Every IP that has used the public trial chat.')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className='space-y-2'>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className='h-16 w-full rounded-lg' />
          ))}
        </div>
      ) : usages.length === 0 ? (
        <p className='text-muted-foreground py-12 text-center text-sm'>
          {t('No trial chat activity yet.')}
        </p>
      ) : (
        <div className='space-y-2'>
          {usages.map((u) => (
            <div
              key={u.ip}
              className='border-border/50 bg-card flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between'
            >
              <div>
                <p className='font-mono text-sm font-medium'>{u.ip}</p>
                <p className='text-muted-foreground text-xs'>
                  {t('{{count}} questions', { count: u.count })} ·{' '}
                  {t('{{tokens}} tokens', { tokens: u.total_tokens })} ·{' '}
                  {t('last used')}: {formatTime(u.last_used_at)}
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setViewingIp(u.ip)}
                >
                  <MessagesSquare className='mr-1.5 size-3.5' />
                  {t('View chat')}
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='text-destructive hover:bg-destructive/10 hover:text-destructive'
                  onClick={() => setResetTarget(u)}
                >
                  <RotateCcw className='mr-1.5 size-3.5' />
                  {t('Reset')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TrialMessagesDialog
        ip={viewingIp}
        open={!!viewingIp}
        onOpenChange={(open) => !open && setViewingIp(null)}
      />

      <ConfirmDialog
        destructive
        open={!!resetTarget}
        onOpenChange={(open) => !open && setResetTarget(null)}
        title={t('Reset this IP?')}
        desc={t(
          'This IP will be able to use the free trial chat again (10 more questions). This cannot be undone.'
        )}
        confirmText={t('Reset')}
        handleConfirm={handleReset}
        isLoading={resetting}
      />
    </div>
  )
}
